import type {
  OriginalCompatCheckpointBody,
  OriginalCompatCheckpointMetadata,
  OriginalCompatCommitAudit,
  OriginalCompatLoadedProject,
} from '../compat/compatTypes';
import { buildCommitKey } from '../compat/compatTypes';
import { buildApprovedBlueprintState, buildApprovedRoadmapState } from '../compat/compatPersistence.service';
import { logOriginalCompatCommit } from '../compat/compatLogging.service';
import type {
  CommitChapterOutcome,
  OriginalEngineCheckpoint,
  OriginalEngineCommitResult,
  OriginalEngineCompatRepository,
  OriginalEngineProjectState,
  OriginalEngineSequelRepository,
  OriginalEngineSequelWorkspaceInput,
} from '../types';
import type { Ledger, StoryBible } from '../../types';

export function stripExperimentalLedgerFields<T extends { continuity_objects?: unknown; physical_continuity_obligations?: unknown }>(
  ledger: T,
): T {
  const { continuity_objects: _co, physical_continuity_obligations: _po, ...rest } = ledger;
  return rest as T;
}

type CommitRecord = {
  status: 'complete' | 'storage_complete' | 'pending_metadata';
  nextChapterNumber?: number;
  result?: OriginalEngineCommitResult;
};

export class InMemoryOriginalEngineProjectRepository implements OriginalEngineCompatRepository {
  private readonly projects = new Map<string, OriginalEngineProjectState & { metadata?: Record<string, unknown> }>();
  readonly checkpoints: OriginalEngineCheckpoint[] = [];
  readonly commits: OriginalEngineCommitResult[] = [];
  private stageCheckpoints = new Map<string, { metadata: OriginalCompatCheckpointMetadata; body: OriginalCompatCheckpointBody }>();
  private commitRecords = new Map<string, CommitRecord>();
  private checkpointFailures = false;
  private commitFailures = false;
  private metadataCommitFailures = false;

  constructor(seed?: Record<string, OriginalEngineProjectState>) {
    if (seed) {
      for (const [id, state] of Object.entries(seed)) {
        this.projects.set(id, structuredClone(state));
      }
    }
  }

  setCommitFailure(enabled: boolean): void {
    this.commitFailures = enabled;
  }

  setMetadataCommitFailure(enabled: boolean): void {
    this.metadataCommitFailures = enabled;
  }

  setCheckpointFailure(enabled: boolean): void {
    this.checkpointFailures = enabled;
  }

  seedProject(state: OriginalEngineProjectState): void {
    this.projects.set(state.projectId, structuredClone(state));
  }

  async loadProject(projectId: string): Promise<OriginalEngineProjectState> {
    const state = this.projects.get(projectId);
    if (!state) throw new Error(`Project not found: ${projectId}`);
    return structuredClone(state);
  }

  async loadFullProject(projectId: string): Promise<OriginalCompatLoadedProject> {
    const state = await this.loadProject(projectId);
    const cp = await this.loadRecoverableCheckpoint(projectId, state.currentChapterNumber);
    return {
      projectId,
      metadata: state.metadata || {},
      storyBible: state.storyBible,
      ledger: state.ledger,
      chapters: state.chapters,
      currentChapterNumber: state.currentChapterNumber,
      volume: state.storyBible.volume,
      seriesId: state.storyBible.seriesId,
      entitlementState: {
        exportUnlocked: state.storyBible.exportUnlocked,
        maxChapters: state.storyBible.targetChapterCount,
      },
      recoverableCheckpoint: cp?.metadata || null,
      roadmapChapterCount: state.storyBible.chapterPlans?.length || 0,
    };
  }

  async persistBlueprintApproval(projectId: string, storyBible: StoryBible, ledger: Ledger): Promise<void> {
    const existing = this.projects.get(projectId);
    if (!existing) throw new Error(`Project not found: ${projectId}`);
    const approved = buildApprovedBlueprintState({ storyBible, ledger });
    this.projects.set(projectId, { ...existing, storyBible: approved.storyBible, ledger: approved.ledger });
  }

  async persistRoadmapApproval(projectId: string, storyBible: StoryBible, ledger: Ledger): Promise<void> {
    const existing = this.projects.get(projectId);
    if (!existing) throw new Error(`Project not found: ${projectId}`);
    const approved = buildApprovedRoadmapState({
      storyBible,
      ledger,
      chapterPlans: storyBible.chapterPlans || [],
    });
    if (approved.validationError) throw new Error(approved.validationError);
    this.projects.set(projectId, { ...existing, storyBible: approved.storyBible, ledger: approved.ledger });
  }

  async saveStageCheckpoint(body: OriginalCompatCheckpointBody, storagePath: string): Promise<void> {
    if (this.checkpointFailures) throw new Error('Simulated checkpoint failure');
    const { chapterDraft: _d, inspectorResult: _i, editorResult: _e, publicationResult: _p, updatedLedger: _l, loopState: _s, ...meta } = body;
    const metadata = { ...meta, storagePath } as OriginalCompatCheckpointMetadata;
    this.stageCheckpoints.set(`${body.projectId}:${body.chapterNumber}`, { metadata, body: structuredClone(body) });
  }

  async loadRecoverableCheckpoint(projectId: string, chapterNumber: number) {
    const key = `${projectId}:${chapterNumber}`;
    const cp = this.stageCheckpoints.get(key);
    if (!cp || cp.metadata.deletedAt) return null;
    return { metadata: structuredClone(cp.metadata), body: structuredClone(cp.body) };
  }

  async discardCheckpoint(projectId: string, chapterNumber: number): Promise<void> {
    const key = `${projectId}:${chapterNumber}`;
    const cp = this.stageCheckpoints.get(key);
    if (cp) {
      cp.metadata.deletedAt = new Date().toISOString();
    }
  }

  async getCommitRecord(commitKey: string) {
    const rec = this.commitRecords.get(commitKey);
    return rec ? { ...rec } : null;
  }

  async saveCheckpoint(checkpoint: OriginalEngineCheckpoint): Promise<void> {
    if (this.checkpointFailures) throw new Error('Simulated checkpoint failure');
    this.checkpoints.push(structuredClone(checkpoint));
  }

  async commitChapter(result: OriginalEngineCommitResult): Promise<CommitChapterOutcome> {
    const commitKey = buildCommitKey(result);
    const existing = this.commitRecords.get(commitKey);
    if (existing?.status === 'complete') {
      const audit: OriginalCompatCommitAudit = {
        projectId: result.projectId,
        chapterNumber: result.chapterNumber,
        runId: result.runId,
        commitKey,
        chapterStorageStatus: 'skipped',
        firestoreStatus: 'skipped',
        idempotentReplay: true,
        reservationAction: 'consume',
      };
      logOriginalCompatCommit(audit);
      return { committed: true, idempotentReplay: true, audit };
    }

    if (this.commitFailures) throw new Error('Simulated commit failure');

    this.commits.push(structuredClone(result));
    if (existing?.status !== 'storage_complete') {
      this.commitRecords.set(commitKey, { status: 'storage_complete', result: structuredClone(result) });
    }

    if (this.metadataCommitFailures) {
      throw new Error('Simulated metadata commit failure');
    }

    const project = this.projects.get(result.projectId);
    if (!project) throw new Error(`Project not found: ${result.projectId}`);
    this.projects.set(result.projectId, {
      ...project,
      storyBible: structuredClone(result.storyBible),
      ledger: structuredClone(result.ledger),
      chapters: structuredClone(result.chapters),
      currentChapterNumber: result.nextChapterNumber,
    });
    this.commitRecords.set(commitKey, {
      status: 'complete',
      nextChapterNumber: result.nextChapterNumber,
      result: structuredClone(result),
    });
    this.stageCheckpoints.delete(`${result.projectId}:${result.chapterNumber}`);

    const audit: OriginalCompatCommitAudit = {
      projectId: result.projectId,
      chapterNumber: result.chapterNumber,
      runId: result.runId,
      commitKey,
      chapterStorageStatus: 'written',
      firestoreStatus: 'written',
      idempotentReplay: false,
      reservationAction: 'consume',
    };
    logOriginalCompatCommit(audit);
    return { committed: true, idempotentReplay: false, audit };
  }

  async retryCommitPending(result: OriginalEngineCommitResult): Promise<CommitChapterOutcome> {
    const commitKey = buildCommitKey(result);
    const existing = this.commitRecords.get(commitKey);
    if (existing?.status === 'complete') {
      return this.commitChapter(result);
    }
    this.metadataCommitFailures = false;
    return this.commitChapter(result);
  }

  async deleteSuccessfulCheckpoint(projectId: string, chapterNumber: number): Promise<void> {
    await this.discardCheckpoint(projectId, chapterNumber);
    const idx = this.checkpoints.findIndex((c) => c.projectId === projectId && c.chapterNumber === chapterNumber);
    if (idx >= 0) this.checkpoints.splice(idx, 1);
  }
}

export class InMemoryOriginalEngineSequelRepository
  extends InMemoryOriginalEngineProjectRepository
  implements OriginalEngineSequelRepository
{
  private sequelWorkspaces: OriginalEngineSequelWorkspaceInput[] = [];

  async verifySourceVolumeComplete(projectId: string): Promise<boolean> {
    const state = await this.loadProject(projectId);
    return state.chapters.length > 0 && state.currentChapterNumber > 1;
  }

  async createSequelWorkspace(input: OriginalEngineSequelWorkspaceInput): Promise<void> {
    this.sequelWorkspaces.push(structuredClone(input));
    this.seedProject({
      projectId: input.newProjectId,
      storyBible: structuredClone(input.storyBible),
      ledger: structuredClone(input.ledger),
      chapters: [],
      currentChapterNumber: 1,
    });
  }

  getSequelWorkspaces(): OriginalEngineSequelWorkspaceInput[] {
    return [...this.sequelWorkspaces];
  }
}
